import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '../components/ui/Button';
import { UnifiedHeader } from '../components/layout/UnifiedHeader';
import { useAuth } from '../hooks/useAuth';
import { Logo } from '../components/ui/Logo';
import { LoginFormData } from '../types/auth';
import { SEO } from '../components/seo/SEO';
import { PAGE_META } from '../config/seo';
import { safeInternalPath } from '../utils/safeInternalPath';

const loginSchema = z.object({
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address'),
  password: z
    .string()
    .min(1, 'Password is required')
    .min(6, 'Password must be at least 6 characters')
});

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, isLoading, error, clearError, isAuthenticated } = useAuth();

  const [showPassword, setShowPassword] = useState(false);

  // Get redirect URL from location state (set by ProtectedRoute) or search params, default to home.
  // Keep search + hash: deep links like /gallery/albums/{id}?media={id} must
  // survive the login round-trip, not just the pathname. safeInternalPath
  // pins the target to an in-app path (no open redirect via ?redirect=).
  const fromLocation = location.state?.from;
  const from = fromLocation?.pathname
    ? `${fromLocation.pathname}${fromLocation.search || ''}${fromLocation.hash || ''}`
    : searchParams.get('redirect');
  const redirectTo = safeInternalPath(from);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, navigate, redirectTo]);

  const {
    register: registerForm,
    handleSubmit,
    formState: { errors, isValid },
    clearErrors
  } = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    mode: 'onChange'
  });

  const handleFormSubmit = async (data: LoginFormData) => {
    try {
      clearError();
      clearErrors();
      
      await login(data);
      
      // Only navigate on successful login
      navigate(redirectTo, { replace: true });
    } catch (error) {
      // Login failed - error is already set in useAuth hook
      // Do not navigate - stay on login page to show error
    }
  };

  // Skip link for keyboard navigation
  const skipToMain = () => {
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.focus();
    }
  };

  return (
    <div className="min-h-screen bg-surface-0">
      <SEO title={PAGE_META['/login'].title} description={PAGE_META['/login'].description} path="/login" />
      {/* Skip to main content link for screen readers */}
      <button
        onClick={skipToMain}
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-brand text-white px-4 py-2 rounded-md z-50"
      >
        Skip to main content
      </button>

      {/* Header */}
      <UnifiedHeader />

      {/* Main Content */}
      <main id="main-content" className="flex items-center justify-center px-4 py-12 pt-28 min-h-screen" tabIndex={-1}>
        <div className="max-w-sm w-full">
          {/* Logo Mark */}
          <div className="flex justify-center mb-8">
            <Logo size="md" />
          </div>

          {/* Card */}
          <div className="bg-white rounded-lg border border-border shadow-sm p-8">
            {/* Heading */}
            <div className="text-center mb-6">
              <h1 className="text-2xl font-semibold text-text-primary mb-2">
                Sign in
              </h1>
              <p className="text-sm text-text-secondary">
                Access your family portal
              </p>
            </div>

            {/* General Error Message */}
            {error && (
              <div 
                className={`mb-6 border rounded-lg p-4 ${
                  error.message.includes('pending verification') || error.message.includes('administrator')
                    ? 'bg-amber-50 border-amber-200'
                    : 'bg-red-50 border-red-200'
                }`}
                role="alert"
                aria-live="polite"
              >
                <div className="flex items-start">
                  <span 
                    className={`mr-3 mt-0.5 text-lg ${
                      error.message.includes('pending verification') || error.message.includes('administrator')
                        ? 'text-amber-600'
                        : 'text-red-600'
                    }`} 
                    aria-hidden="true"
                  >
                    {error.message.includes('pending verification') || error.message.includes('administrator') ? '⏳' : '⚠'}
                  </span>
                  <div>
                    <p 
                      className={`font-medium text-sm ${
                        error.message.includes('pending verification') || error.message.includes('administrator')
                          ? 'text-amber-800'
                          : 'text-red-800'
                      }`}
                    >
                      {error.message.includes('pending verification') || error.message.includes('administrator')
                        ? 'Account Under Review'
                        : 'Authentication Error'
                      }
                    </p>
                    <p 
                      className={`text-sm mt-1 ${
                        error.message.includes('pending verification') || error.message.includes('administrator')
                          ? 'text-amber-700'
                          : 'text-red-700'
                      }`}
                    >
                      {error.message}
                    </p>
                    {(error.message.includes('pending verification') || error.message.includes('administrator')) && (
                      <div className="mt-3 pt-3 border-t border-amber-200">
                        <p className="text-amber-700 text-xs leading-relaxed">
                          <strong>What happens next:</strong><br/>
                          • A family administrator will review your registration within 24-48 hours<br/>
                          • You'll receive an email confirmation once approved<br/>
                          • Check your spam folder if you don't see our email
                        </p>
                        <p className="text-amber-600 text-xs mt-2">
                          Questions? Contact us at{' '}
                          <a 
                            href="mailto:admin@reddfamily.org" 
                            className="underline hover:text-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500 rounded"
                          >
                            admin@reddfamily.org
                          </a>
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Login Form */}
            <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-5">
              <div>
                <label htmlFor="login-email" className="block text-sm font-medium text-text-primary mb-1.5">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-4 w-4 text-text-tertiary" aria-hidden="true" />
                  </div>
                  <input
                    {...registerForm('email')}
                    id="login-email"
                    type="email"
                    autoComplete="email"
                    required
                    disabled={isLoading}
                    className="block w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 disabled:bg-surface-0 disabled:text-text-tertiary transition-colors"
                    placeholder="Enter your email"
                    aria-invalid={errors.email ? 'true' : 'false'}
                    aria-describedby={errors.email ? 'login-email-error' : undefined}
                  />
                </div>
                {errors.email && (
                  <p id="login-email-error" className="mt-1.5 text-sm text-error flex items-center gap-1" role="alert">
                    <span className="text-error" aria-hidden="true">⚠</span>
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="login-password" className="block text-sm font-medium text-text-primary mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-4 w-4 text-text-tertiary" aria-hidden="true" />
                  </div>
                  <input
                    {...registerForm('password')}
                    id="login-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="current-password"
                    required
                    disabled={isLoading}
                    className="block w-full pl-10 pr-10 py-2.5 border border-border rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 disabled:bg-surface-0 disabled:text-text-tertiary transition-colors"
                    placeholder="Enter your password"
                    aria-invalid={errors.password ? 'true' : 'false'}
                    aria-describedby={errors.password ? 'login-password-error' : undefined}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-text-tertiary hover:text-text-secondary focus:outline-none focus:text-text-secondary transition-colors"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                {errors.password && (
                  <p id="login-password-error" className="mt-1.5 text-sm text-error flex items-center gap-1" role="alert">
                    <span className="text-error" aria-hidden="true">⚠</span>
                    {errors.password.message}
                  </p>
                )}
              </div>

              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-brand focus:ring-brand border-border rounded"
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-text-secondary">
                    Remember me
                  </label>
                </div>

                <div className="text-sm">
                  <Link
                    to="/forgot-password"
                    className="font-medium text-brand hover:text-brand-hover focus:outline-none focus:ring-2 focus:ring-brand/20 rounded transition-colors"
                  >
                    Forgot password?
                  </Link>
                </div>
              </div>

              <Button
                type="submit"
                size="lg"
                fullWidth
                loading={isLoading}
                disabled={!isValid || isLoading}
                className="bg-brand text-white hover:bg-brand-hover transition-colors"
              >
                Sign In
              </Button>
            </form>

            {/* Register Link */}
            <div className="mt-6 pt-6 border-t border-border text-center">
              <p className="text-text-secondary text-sm">
                Don't have an account?{' '}
                <Link
                  to="/register"
                  className="font-medium text-brand hover:text-brand-hover focus:outline-none focus:ring-2 focus:ring-brand/20 rounded transition-colors"
                >
                  Register here
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export { LoginPage };
